import type { RefObject } from 'react';

/** Uma camada do Base UI ABERTA e não saindo de cena (`data-open` sem
 *  `data-ending-style`): popover ou folha. */
const OPEN_LAYER = '[role="dialog"][data-open]:not([data-ending-style])';

/**
 * A devolução de foco de uma camada (folha, popover) que RESPEITA um foco já
 * movido: se, na hora de fechar, o foco está num elemento vivo fora do `body`,
 * a camada não o rouba de volta — é o caso do preenchimento guiado, em que a
 * folha do campo fecha enquanto a do campo seguinte já tomou o foco, e o do
 * Confirmar que pousa o foco no Buscar antes de a folha sair de cena. Só o
 * foco DERRUBADO (a camada desmontou com o foco dentro, e o navegador o
 * devolveu ao `body`) volta ao gatilho — e nem ele, se OUTRA camada já está
 * aberta em cena fora do gatilho: duas folhas trocando de lugar não têm ordem
 * garantida, e a seguinte pode ainda não ter tomado o foco quando esta fecha;
 * devolvê-lo ao gatilho, atrás da modal, faria a modal puxá-lo de volta e
 * perdê-lo no `body`. Uma camada que CONTÉM o gatilho (o overlay em que a
 * barra mora) não conta: ela é a casa, não a próxima parada.
 *
 * Existe porque o Base UI, com um `finalFocus` EXPLÍCITO (ref ou elemento),
 * foca o alvo sempre — a regra "não devolver se o foco já foi pra outro lugar"
 * dele só vale para o `finalFocus` booleano. A função é avaliada NA HORA do
 * fechamento, então a decisão lê o DOM daquele instante, não o do render.
 *
 * O foco DERRUBADO não é devolvido pelo Base UI, e sim por esta função, numa
 * microtask, relendo o DOM: o Base UI resolve o alvo ao desmontar e só o foca
 * na microtask seguinte — e, no meio, a folha do campo seguinte pode montar e
 * focar a busca dela (#4185). Decidir na avaliação devolvia o foco ao gatilho
 * de trás e tirava o cursor da busca que acabara de abrir.
 */
export function returnFocusUnlessMoved(fallback: RefObject<HTMLElement | null>): () => false {
  const moved = (): boolean => {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body && active.isConnected;
  };
  const nextLayer = (trigger: HTMLElement | null): boolean =>
    Array.from(document.querySelectorAll(OPEN_LAYER)).some(
      (layer) => !trigger || !layer.contains(trigger),
    );
  return () => {
    const trigger = fallback.current;
    if (trigger && !moved() && !nextLayer(trigger)) {
      queueMicrotask(() => {
        if (!moved() && !nextLayer(trigger) && trigger.isConnected) {
          trigger.focus({ preventScroll: true });
        }
      });
    }
    return false;
  };
}
